import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Circle, OrgMember } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import {
  listCircles,
  createCircle,
  renameCircle,
  deleteCircle,
  addCircleMember,
  removeCircleMember,
  getOrg,
} from '../../api/client';
import Button from '../../components/Button';

export function CirclesTab() {
  const { orgId } = useParams<{ orgId: string }>();
  const { orgs } = useSafe();
  const myOrg = orgs.find((o) => o.id === orgId);
  const isAdmin = myOrg?.role === 'admin';

  const [circles, setCircles]   = useState<Circle[]>([]);
  const [members, setMembers]   = useState<OrgMember[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Create
  const [newCircleName, setNewCircleName] = useState('');
  const [creating, setCreating]           = useState(false);

  // Delete
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting]               = useState(false);

  // Rename
  const [renamingId, setRenamingId]   = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming]       = useState(false);

  // Member toggle — tracks the in-flight `${circleId}:${userId}` key
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const [circlesResult, orgResult] = await Promise.all([
        listCircles(orgId),
        getOrg(orgId),
      ]);
      if (circlesResult.ok) {
        setCircles(circlesResult.data.circles);
      } else {
        setError(circlesResult.error);
      }
      if (orgResult.ok) {
        setMembers(orgResult.data.members);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId || !newCircleName.trim()) return;
    setCreating(true);
    try {
      const result = await createCircle(orgId, newCircleName.trim());
      if (result.ok) {
        setNewCircleName('');
        await reload();
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(circleId: string) {
    if (!orgId) return;
    setDeleting(true);
    try {
      const result = await deleteCircle(orgId, circleId);
      if (result.ok) {
        setConfirmDeleteId(null);
        await reload();
      }
    } finally {
      setDeleting(false);
    }
  }

  async function handleRename(circleId: string) {
    if (!orgId || !renameValue.trim()) return;
    setRenaming(true);
    try {
      const result = await renameCircle(orgId, circleId, renameValue.trim());
      if (result.ok) {
        setRenamingId(null);
        setRenameValue('');
        await reload();
      } else {
        setError(result.error);
      }
    } catch {
      setError('Failed to rename circle');
    } finally {
      setRenaming(false);
    }
  }

  async function handleToggleMember(
    circleId: string,
    userId: string,
    isCurrentlyMember: boolean,
  ) {
    if (!orgId) return;
    const key = `${circleId}:${userId}`;
    setTogglingKey(key);
    try {
      if (isCurrentlyMember) {
        await removeCircleMember(orgId, circleId, userId);
      } else {
        await addCircleMember(orgId, circleId, userId);
      }
      await reload();
    } finally {
      setTogglingKey(null);
    }
  }

  if (loading) {
    return <div className="text-hbs-text-mute text-sm">Loading…</div>;
  }

  if (error) {
    return <div className="text-hbs-text-mute text-sm">{error}</div>;
  }

  // ── Non-admin: read-only ────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-sm text-hbs-text-mute">
          Circles limit access to binders. A circle lets only some family members see a
          binder — the ones in the circle — instead of everyone in the family.
        </p>

        <section>
          <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">
            Circles
          </h2>
          {circles.length === 0 ? (
            <p className="text-sm text-hbs-text-mute">No circles in this family yet.</p>
          ) : (
            <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border overflow-hidden">
              {circles.map((circle, idx) => (
                <div
                  key={circle.id}
                  className={`flex items-center gap-3 px-4 py-3 ${
                    idx < circles.length - 1 ? 'border-b border-hbs-border' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-hbs-text">{circle.name}</p>
                    <p className="text-xs text-hbs-text-mute">
                      {circle.memberCount} member{circle.memberCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  // ── Admin view ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-hbs-text-mute">
        Circles limit access to binders. Use a circle when you don't want everyone in the
        family to see a binder — only the circle's members can see a binder shared to it.
      </p>

      {/* Circle list */}
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">
          Circles
        </h2>
        {circles.length === 0 ? (
          <p className="text-sm text-hbs-text-mute mb-3">No circles yet. Create one below.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {circles.map((circle) => {
              const isConfirmingDelete = confirmDeleteId === circle.id;
              const isRenaming = renamingId === circle.id;

              return (
                <div key={circle.id} className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-4">
                  {/* Header row */}
                  <div className="flex items-start gap-2 mb-3">
                    {isRenaming ? (
                      <>
                        <input
                          aria-label="Rename circle"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="flex-1 bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2 text-sm text-hbs-text focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
                        />
                        <button
                          id={`circle-rename-save-${circle.id}`}
                        onClick={() => void handleRename(circle.id)}
                          disabled={renaming}
                          className="px-2.5 py-1.5 text-xs bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md transition-colors disabled:opacity-50"
                        >
                          {renaming ? '…' : 'Save'}
                        </button>
                        <button
                          id={`circle-rename-cancel-${circle.id}`}
                          onClick={() => {
                            setRenamingId(null);
                            setRenameValue('');
                          }}
                          className="px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text rounded-hbs-md transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-hbs-text">{circle.name}</p>
                          <p className="text-xs text-hbs-text-mute">
                            {circle.memberCount} member{circle.memberCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <button
                          id={`circle-rename-${circle.id}`}
                          onClick={() => {
                            setRenamingId(circle.id);
                            setRenameValue(circle.name);
                          }}
                          className="flex-shrink-0 px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors"
                        >
                          Rename
                        </button>
                        <button
                          id={`circle-delete-${circle.id}`}
                        onClick={() => setConfirmDeleteId(circle.id)}
                          className="flex-shrink-0 px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-danger hover:bg-hbs-danger/10 rounded-hbs-md transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>

                  {/* Delete confirm */}
                  {isConfirmingDelete && (
                    <div className="mb-3 p-3 bg-hbs-danger/10 border border-hbs-danger/20 rounded-hbs-md">
                      <p className="text-sm text-hbs-text mb-2">
                        Delete this circle? Items shared with it will become visible to the whole family.
                      </p>
                      <div className="flex gap-2">
                        <button
                          id={`circle-delete-confirm-${circle.id}`}
                          onClick={() => void handleDelete(circle.id)}
                          disabled={deleting}
                          className="px-2.5 py-1.5 text-xs bg-hbs-danger/20 hover:bg-hbs-danger/30 text-hbs-danger rounded-hbs-md transition-colors disabled:opacity-50"
                        >
                          {deleting ? '…' : 'Confirm delete'}
                        </button>
                        <button
                          id={`circle-delete-cancel-${circle.id}`}
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-2.5 py-1.5 text-xs bg-hbs-surface-alt hover:bg-hbs-surface-alt text-hbs-text rounded-hbs-md transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Member checklist */}
                  {members.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-hbs-text-mute mb-2">Members</h3>
                      <ul className="space-y-1">
                        {members.map((member) => {
                          const isMember = member.circleIds?.includes(circle.id) ?? false;
                          const toggleKey = `${circle.id}:${member.userId}`;
                          return (
                            <li key={member.userId} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`circle-${circle.id}-member-${member.userId}`}
                                checked={isMember}
                                disabled={togglingKey === toggleKey}
                                onChange={() =>
                                  void handleToggleMember(circle.id, member.userId, isMember)
                                }
                                className="rounded text-hbs-primary focus:ring-hbs-primary"
                              />
                              <label
                                htmlFor={`circle-${circle.id}-member-${member.userId}`}
                                className="text-sm text-hbs-text"
                              >
                                {member.email}
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Create circle */}
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">
          Create Circle
        </h2>
        <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-4">
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div>
              <label
                htmlFor="new-circle-name"
                className="block text-xs font-medium text-hbs-text-mute mb-1"
              >
                Circle name
              </label>
              <input
                id="new-circle-name"
                value={newCircleName}
                onChange={(e) => setNewCircleName(e.target.value)}
                placeholder="e.g. Parents, Kids, Work"
                required
                className="w-full bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text placeholder-hbs-text-mute focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
              />
            </div>
            <Button type="submit" loading={creating}>
              Create circle
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
